import { ArrowRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { IngestionDiagram, RetrievalFusionDiagram, CitedAnswerDiagram } from "@/components/site/Diagrams";

const STEPS = [
  { n: "01", t: "Ingest", d: "Point Smritium at a folder. Documents are hashed on content, deduplicated, and stored — idempotent and reproducible.", tag: "content-addressed" },
  { n: "02", t: "Chunk", d: "Deterministic chunking: the same input always yields the same chunk IDs, so citations stay stable across re-indexes.", tag: "deterministic" },
  { n: "03", t: "Rehydrate", d: "The Context Rehydrator fuses semantic, temporal and relational signals into one ranked context window.", tag: "3 signals fused" },
  { n: "04", t: "Answer", d: "A local (or optional remote) model answers with citations to source chunks — or abstains when confidence is low.", tag: "cited · offline" },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <Seo title="Smritium — How it works" description="Ingest, chunk, rehydrate, answer. How Smritium turns a folder into cited, offline memory." />
      <PageHero
        eyebrow="Smritium · How it works"
        title="Install the engine, point it at a folder, query cited memory."
        lead="No black-box cloud. A single deterministic pipeline turns documents into attributed, reproducible memory you can run from your terminal, your code, or an MCP-connected agent."
      />

      <Section>
        <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s) => (
            <Item key={s.n} className="bg-ink p-7">
              <div className="font-mono text-sm text-saffron">{s.n}</div>
              <h3 className="mt-5 font-serif text-2xl text-bone">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{s.d}</p>
              <div className="mt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-mist">{s.tag}</div>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal className="border border-white/10 bg-white/[0.015] p-8">
            <IngestionDiagram className="text-bone/70" />
            <p className="mt-4 font-mono text-xs text-mist">Fig. 1 — Deterministic ingestion pipeline.</p>
          </Reveal>
          <Reveal delay={0.1} className="border border-white/10 bg-white/[0.015] p-8">
            <RetrievalFusionDiagram className="text-bone/70" />
            <p className="mt-4 font-mono text-xs text-mist">Fig. 2 — The Context Rehydrator fuses three signals.</p>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>The result</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              Cited, offline, zero bytes leave the device.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              Every answer traces to its source chunks by content-addressed ID. When confidence is low, Smritium
              abstains rather than inventing a citation.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 bg-ink-850 p-6 font-mono text-[13px] leading-relaxed">
              <div className="text-mist">$ smritium ask "what did we agree on latency?"</div>
              <div className="mt-4 text-bone">Answer: A 6-hour delivery SLA, relaxed to 12 hours during the pilot.</div>
              <div className="mt-3 text-mist">
                Sources: <span className="text-bone">[1] sync.md (chunk a3f2c9)</span> · <span className="text-bone">[2] terms.md (chunk 77d01b)</span>
              </div>
              <div className="mt-4 text-signal">✓ answered offline · 0 bytes left the device</div>
            </div>
            <div className="mt-8 border border-white/10 bg-white/[0.015] p-8">
              <CitedAnswerDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 3 — Cite or abstain.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            Read the security model behind local-first.
          </h2>
          <CTAButton to="/smritium/security" variant="outline">
            Security <ArrowRight className="h-4 w-4" />
          </CTAButton>
        </Reveal>
      </Section>
    </div>
  );
}
